import { toUserViewModelList } from '../../../core/client/client.core.js';

export class UserListEventAdapter {
  #apiAdapter;
  #userListComponent;
  #notificationComponent;
  #reloadPage;
  /** Client slug the listed users are filtered on; empty lists every user. */
  #clientSlug = '';

  /**
   * @param {() => void} [reloadPage] reloads the app so index.js shows the login screen
   */
  constructor(apiAdapter, userListComponent, notificationComponent, reloadPage = location.reload.bind(location)) {
    this.#apiAdapter = apiAdapter;
    this.#userListComponent = userListComponent;
    this.#notificationComponent = notificationComponent;
    this.#reloadPage = reloadPage;
  }

  connect() {
    document.addEventListener('user-filter', this.#onFilter);
    document.addEventListener('user-delete', this.#onDelete);
    document.addEventListener('user-reset-password', this.#onResetPassword);
  }

  disconnect() {
    document.removeEventListener('user-filter', this.#onFilter);
    document.removeEventListener('user-delete', this.#onDelete);
    document.removeEventListener('user-reset-password', this.#onResetPassword);
  }

  /**
   * Loads the users of one client, or every user for an empty slug, and shows that slug in the
   * filter input so the input always describes the list below it.
   */
  async loadUsers(clientSlug = '') {
    this.#clientSlug = clientSlug;
    this.#userListComponent.filterSlug = clientSlug;
    this.#userListComponent.loading = true;
    try {
      const apiUsers = await this.#apiAdapter.getUsers(clientSlug || undefined);
      this.#userListComponent.users = toUserViewModelList(apiUsers);
      this.#notificationComponent.clearError();
    } catch (error) {
      this.#handleError(error, 'Could not load users');
    } finally {
      this.#userListComponent.loading = false;
    }
  }

  async loadClientOptions() {
    try {
      const clients = await this.#apiAdapter.getAllClients();
      this.#userListComponent.clientOptions = clients.map((c) => c.slug);
    } catch (error) {
      this.#handleError(error, 'Could not load the client filter options');
    }
  }

  #onFilter = async (event) => {
    await this.loadUsers(event.detail.clientSlug);
  };

  #onDelete = async (event) => {
    const { id } = event.detail;
    this.#userListComponent.loading = true;
    try {
      await this.#apiAdapter.deleteUser(id);
      this.#notificationComponent.show('User deleted', 'success');
      await this.loadUsers(this.#clientSlug);
    } catch (error) {
      this.#handleError(error, 'Could not delete user');
    } finally {
      this.#userListComponent.loading = false;
    }
  };

  /**
   * Closes the reset password dialog only once the password is saved, so a rejected one can be
   * corrected; the dialog itself says why it was rejected.
   */
  #onResetPassword = async (event) => {
    const { id, newPassword } = event.detail;
    this.#userListComponent.loading = true;
    try {
      await this.#apiAdapter.resetPassword(id, { newPassword });
      this.#userListComponent.closeResetPasswordDialog();
      this.#notificationComponent.show('Password reset successfully', 'success');
    } catch (error) {
      this.#handleError(error, 'Could not reset the password', {
        show: (message) => this.#userListComponent.showResetPasswordError(message),
        close: () => this.#userListComponent.closeResetPasswordDialog(),
      });
    } finally {
      this.#userListComponent.loading = false;
    }
  };

  /**
   * Shows why a request failed, once: next to what the user was doing when `inline` can show it
   * there, else in a toast. A dialog closed while the request ran can no longer show it, so the
   * toast does. An expired session always goes to the toast, since the app reloads to the login
   * screen right after; an open dialog closes first, as its backdrop would cover the toast.
   *
   * @param {{status?: number, message: string}} error
   * @param {string} action what failed, shown before the server's message
   * @param {{show: (message: string) => boolean, close?: () => void}} [inline] `show` returns
   *     whether it could show the message; `close` closes the dialog that shows it
   */
  #handleError(error, action, inline) {
    if (error.status === 401) {
      inline?.close?.();
      this.#notificationComponent.show('Session expired. Please login again.', 'error');
      setTimeout(() => this.#reloadPage(), 2000);
    } else if (!inline?.show(`${action}: ${error.message}`)) {
      this.#notificationComponent.show(`${action}: ${error.message}`, 'error');
    }
  }
}
